import {
  ILinkedTask,
  IMember,
  ITask,
  IMemberContact,
  IMemberAccout,
  IMemberFeedback,
  IMemberForm,
  Encounter,
  IMembershipBenefit,
  IRedeemedBenefit,
  IVoidBenefitRequest,
  IRedeemBenefitRequest,
  IUpdateRedeemedDateRequest
} from 'types';
import ApiService from './api-service';
import humanParser from 'humanparser';
import { QueryStringBuilder } from './query-string-builder';
import settings from 'constants/appsettings.json';
import { convertToMoment } from 'utils/helpers/dateTimeHelper';
import { convertToPhoneNumber } from 'utils/helpers/phoneNumberHelper';

export class MemberService {
  protected apiService: ApiService;
  protected serviceScope: string[];

  constructor(apiService: ApiService) {
    this.apiService = apiService;
    this.serviceScope = settings.serviceScopes.MemberService;
  }

  findMembers(searchString: string = ''): Promise<IMember[]> {
    const queryStringBuilder = new QueryStringBuilder();
    //  is phone number?
    const phoneNumberParseResult = convertToPhoneNumber(searchString);
    if (phoneNumberParseResult && phoneNumberParseResult.isValid()) {
      queryStringBuilder.addParam('phoneNumber', phoneNumberParseResult.number);
    }

    //  is date?
    const now = new Date();
    const date = convertToMoment(searchString);
    if (date.isValid() && date.year() > 1900 && date.year() <= now.getFullYear()) {
      queryStringBuilder.addParam('dateOfBirth', date.format('YYYY-MM-DD'));
    }

    //  is name?
    if (!queryStringBuilder.hasParams()) {
      const human = humanParser.parseName(searchString);
      if (human && human.firstName && human.lastName) {
        queryStringBuilder.addParam('searchFirstName', human.firstName);
        queryStringBuilder.addParam('searchLastName', human.lastName);
      }
    }

    //  catch-all
    if (!queryStringBuilder.hasParams()) {
      queryStringBuilder.addParam('searchString', searchString.trim());
    }

    return this.apiService.get(this.serviceScope, `member/v1/search?${queryStringBuilder.queryString}`).then((r) => r?.data);
  }

  getMember(memberId: number): Promise<IMember> {
    return this.apiService.get(this.serviceScope, `member/v1/${memberId}`).then((r) => r?.data);
  }

  updatePortalAccess(memberId: number, enabled: boolean): Promise<void> {
    return this.apiService.post(this.serviceScope, `member/v1/${memberId}/portal`, { enabled }).then((r) => r?.data);
  }

  findContacts(memberId: number, searchTerm: string): Promise<IMemberContact[]> {
    return this.apiService.post(this.serviceScope, `member/v1/contactSearch`, { memberId, searchTerm }).then((r) => r?.data);
  }

  checkForHIEFile(memberId: number): Promise<IMemberForm> {
    return this.apiService.get(this.serviceScope, `member/v1/${memberId}/hie`).then((r) => r?.data);
  }

  loadMemberTasks(memberId: number): Promise<ITask[]> {
    return this.apiService.get(settings.serviceScopes.TaskService, `tasks/v1/Task/member/${memberId}`).then((r) => r?.data);
  }

  loadMemberAccountTasks(accountId: string): Promise<ILinkedTask[]> {
    return this.apiService
      .get(settings.serviceScopes.TaskService, `tasks/v1/Task/memberAccount/${accountId}/accountTasks`)
      .then((r) => r?.data);
  }

  getMemberAccount(accountId: string): Promise<IMemberAccout> {
    return this.apiService.get(this.serviceScope, `member/v1/Account/${accountId}`).then((r) => r?.data);
  }

  getMemberFeedback(memberId: number): Promise<IMemberFeedback[]> {
    return this.apiService.get(this.serviceScope, `member/v1/getFeedbackForMember?memberId=${memberId}`).then((r) => r?.data);
  }

  getMemberForms(memberId: number): Promise<IMemberForm[]> {
    return this.apiService.get(this.serviceScope, `member/v1/${memberId}/forms`).then((r) => r?.data);
  }

  loadMemberForm(memberId: number, memberFormId: string): Promise<IMemberForm | null> {
    return this.apiService.get(this.serviceScope, `member/v1/${memberId}/form/${memberFormId}`).then((r) => r?.data);
  }

  updateNote(memberId: number, noteContent?: string): Promise<string | null | undefined> {
    return this.apiService.put(this.serviceScope, `member/v1/${memberId}/note`, { noteContent: noteContent }).then((r) => r?.data);
  }

  getMemberEncounter(memberId: number): Promise<Encounter[]> {
    return this.apiService.get(this.serviceScope, `member/v1/getEncountersForMember?memberId=${memberId}`).then((r) => r?.data);
  }

  getMembershipBenefits(memberId: number): Promise<IMembershipBenefit[]> {
    return this.apiService.get(this.serviceScope, `member/v1/Benefit/member/${memberId}`).then((r) => r?.data);
  }

  redeemMembershipBenefit(redeemRequest: IRedeemBenefitRequest): Promise<IRedeemedBenefit> {
    return this.apiService.put(this.serviceScope, `member/v1/Benefit/redeem`, redeemRequest).then((r) => r?.data);
  }

  updateBenefitRedeemedDate(updateRequest: IUpdateRedeemedDateRequest): Promise<IRedeemedBenefit> {
    return this.apiService.put(this.serviceScope, `member/v1/Benefit/updateRedeemed`, updateRequest).then((r) => r?.data);
  }

  voidBenefit(voidRequest: IVoidBenefitRequest): Promise<void> {
    return this.apiService.put(this.serviceScope, `member/v1/Benefit/voidRedeemed`, voidRequest).then((r) => r?.data);
  }
}

export default MemberService;
